import axios from "axios";
import { Buffer } from "buffer";
import zlib from "react-zlib-js";
import moment, { Moment } from "moment";
import { MarketLimitI } from "src/types/eventDetail";
import { EVENTSTATUS, RUNNERSTATUS } from "src/config/constants";
import { parse } from 'tldts';

export const handleResponse = (res: any, isEncypted = false): any => {
  if (res?.status_code === 200 && !res.response_error) {
    return isEncypted ? decodeV1Response(res.data) : res.data;
  }
  throw new Error(res?.status_message || "Unknown error occurred");
};

export const handleCatch = (res: { response: { status: number; data: { status_message: any } } }) => {
  if (res.response.status === 401) {
    localStorage.clear();
    window.location.reload();
  }
  if (res?.response?.data?.status_message) {
    throw res.response.data.status_message;
  } else throw { error: "Something went wrong" };
};

export const getIP = async () => {
  try {
    const res = await axios.get(
      `https://api-bdc.net/data/ip-geolocation-full?key=bdc_655c0adebc9040d891bac02d6ea38dfd`
    );
    if (res && res.data && res.data.ip) return res.data.ip;
    else return "192.168.0.1";
  } catch (error) {
    // console.error('Error fetching data:', error);
    if (error) {
      return "192.168.0.1";
    }
    throw error; // Re-throw the error to propagate it further
  }
  // return "192.168.0.1"
};

const xorEncrypt = (text: string, key: string) => {
  let encrypted = "";
  for (let i = 0; i < text.length; i += 1) {
    encrypted += String.fromCharCode(text.charCodeAt(i) ^ key.charCodeAt(i % key.length));
    // const charCodeText = text.charCodeAt(i);
    // const charCodeKey = key.charCodeAt(i % key.length);
    // const xorResult = (charCodeText | charCodeKey) & ~(charCodeText & charCodeKey);
    // encrypted += String.fromCharCode(xorResult);
  }
  return encrypted;
};

export const encodeV1Response = (response: any) => {
  try {
    const jsonString = JSON.stringify(response);
    const encryptedData = xorEncrypt(jsonString, process.env.REACT_APP_SECRET_KEY || "");
    const responseData = zlib.gzipSync(encryptedData).toString("base64");
    // const responseData = zlib.deflateSync(encryptedData).toString('base64');
    // const responseData = zlib.brotliCompressSync(jsonString).toString('base64');
    return { data: responseData };
  } catch (error) {
    throw "something went wrong";
  }
};

export const xorDecrypt = (encryptedText: string, key: string) => {
  let decrypted = "";
  for (let i = 0; i < encryptedText.length; i++) {
    decrypted += String.fromCharCode(encryptedText.charCodeAt(i) ^ key.charCodeAt(i % key.length));
  }
  return decrypted;
};

export const decodeV1Response = (
  encodeResponse: WithImplicitCoercion<string> | { [Symbol.toPrimitive](hint: "string"): string }
) => {
  const brCompressed = Buffer.from(encodeResponse, "base64");
  const decodeData = zlib.gunzipSync(brCompressed);
  const jsonString = decodeData.toString();
  const key = process.env.REACT_APP_SECRET_KEY;
  const decryptedData = xorDecrypt(jsonString, key || "");
  return JSON.parse(decryptedData);
};

export function convertQueryStringToObject(
  queryString: string | string[][] | Record<string, string> | URLSearchParams | undefined
) {
  const params = new URLSearchParams(queryString);
  const obj: any = {};

  params.forEach((value: any, key: any) => {
    // Check if the key already exists, indicating an array
    if (obj.hasOwnProperty(key)) {
      // If it's not already an array, make it an array
      if (!Array.isArray(obj[key])) {
        obj[key] = [obj[key]];
      }
      obj[key].push(value);
    } else {
      obj[key] = value;
    }
  });

  return obj;
}

export const parseUnixTimeStamp = (value: Moment | null) => {
  try {
    if (!value) {
      return "";
    }
    return moment(value).unix();
  } catch (error) {
    return "";
  }
};

interface ParseNumberI {
  type?: "float" | "int";
  value: any;
  toFixed?: number;
}

export const parseNumber = ({ type = "int", value, toFixed }: ParseNumberI) => {
  try {
    if (type === "int") {
      const nValue: any = parseInt(value, 10);
      if (Number.isNaN(nValue)) {
        return 0;
      }
      return nValue;
    } else if (type === "float") {
      let nValue: any = parseFloat(value);
      if (toFixed) {
        nValue = nValue.toFixed(toFixed);
      }

      if (Number.isNaN(nValue)) {
        return 0;
      }
      return nValue;
    }
    return 0;
  } catch (error) {
    return 0;
  }
};

export const isRacing = (providerId?: string) => {
  return providerId === "7" || providerId === "4339";
};

export const getRandomId = (length: number = 10): string => {
  let text = "";
  const possible = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

  for (let i = 0; i < length; i++) {
    text += possible.charAt(Math.floor(Math.random() * possible.length));
  }

  return text;
};

interface IFindMarketLimit {
  market_limits?: MarketLimitI[];
  currency_id?: string;
}
export function findMarketLimit({ market_limits, currency_id }: IFindMarketLimit) {
  if (currency_id && market_limits) {
    if (market_limits && Array.isArray(market_limits)) {
      const marketLimit = market_limits.find((item: MarketLimitI) => item.currency_id === currency_id);
      return marketLimit;
    }
  } else if (market_limits) {
    if (market_limits && Array.isArray(market_limits)) {
      const firstMarketLimit = market_limits[0];
      return firstMarketLimit;
    }
  }
  return undefined;
}

export interface IGetStatus {
  providerStatus?: string;
  marketStatus?: string;
  provider_id?: string;
}

export const getIsSuspended = ({ providerStatus = "", marketStatus = "", provider_id }: IGetStatus) => {
  if (provider_id && ["1", "2", "7522"].includes(provider_id)) {
    const is = Boolean(
      [EVENTSTATUS.SUSPENDED, EVENTSTATUS.UPCOMING, EVENTSTATUS.CLOSED].includes(providerStatus) ||
      [EVENTSTATUS.SUSPENDED, EVENTSTATUS.UPCOMING, EVENTSTATUS.CLOSED].includes(marketStatus)
    );
    return is;
  }

  return Boolean(
    [EVENTSTATUS.SUSPENDED, EVENTSTATUS.CLOSED].some((s) => s === providerStatus) ||
    [EVENTSTATUS.SUSPENDED, EVENTSTATUS.CLOSED].some((s) => s === marketStatus)
  );
};

export const getIsBallRunning = ({ providerStatus, marketStatus }: IGetStatus) => {
  return Boolean(providerStatus === RUNNERSTATUS.BALLRUNNING || marketStatus === RUNNERSTATUS.BALLRUNNING);
};

/**
 * Formats a number with various suffixes (K, L, M, Cr, B)
 * @param value - The number to format
 * @returns Formatted string with appropriate suffix
 * K (Thousand) - 1,000 = 1K
 * L (Lakh) - 100,000 = 1L
 * M (Million) - 1,000,000 = 1M
 * Cr (Crore) - 10,000,000 = 1Cr
 * B (Billion) - 1,000,000,000 = 1B
 */

const formatDecimal = (num: number) => {
  return Number.isInteger(num) ? num.toString() : num.toFixed(2);
};

export const suffixNumber = (value: any): string => {
  if (Number.isNaN(value) || value === null || value === undefined) return "0";

  const nValue = parseFloat(value);

  // For numbers less than 1000, return with 2 decimal places
  if (nValue < 1000) {
    return formatDecimal(nValue);
  }

  // For thousands (K) - 1,000 to 99,999
  if (nValue < 100000) {
    return formatDecimal(Math.floor((nValue / 1000) * 100) / 100) + "K";
  }

  // For lakhs (L) - 100,000 to 999,999
  if (nValue < 1000000) {
    return formatDecimal(Math.floor((nValue / 100000) * 100) / 100) + "L";
  }

  // For millions (M) - 1,000,000 to 9,999,999
  if (nValue < 10000000) {
    return formatDecimal(Math.floor((nValue / 1000000) * 100) / 100) + "M";
  }

  // For crores (Cr) - 10,000,000 to 999,999,999
  if (nValue < 1000000000) {
    return formatDecimal(Math.floor((nValue / 10000000) * 100) / 100) + "Cr";
  }

  // For billions (B) - 1,000,000,000 and above
  return formatDecimal(Math.floor((nValue / 1000000000) * 100) / 100) + "B";
};

// const testValues = [
//   0, 5, 12.5, 99.99, 999, 999.5,

//   1000, 1500, 9999, 12500, 50000, 99999,

//   100000, 150000, 250000, 500000, 999999,

//   1000000, 1500000, 2500000, 5000000, 9999999,

//   10000000, 15000000, 25000000, 50000000, 999999999,

//   1000000000, 2500000000,
// ];

// testValues.forEach((v) => {
//   console.log(v, "→", suffixNumber(v));
// });

export const hasValue = (value: any) => {
  if (value === null) return false;
  if (value === undefined) return false;

  if (typeof value === "string" || typeof value === "number" || value === 0) return true;

  return true;
};

export const formatAmount = ({ amt }: { amt: string }) => {
  const floatValue = hasValue(amt) && amt.toString() ? amt.toString().split(".")[1] : "";
  const floatVal = floatValue ? parseFloat(floatValue) > 0.1 : false;
  const isFloat = hasValue(amt) && amt.toString().includes(".") ? true : false;
  const nAmt = parseNumber({ value: amt || "0", type: "float", toFixed: isFloat ? 2 : 0 });

  const fullSuffix = suffixNumber(nAmt);
  const suffix = fullSuffix.replace(/[^a-zA-Z]/g, "");

  const value = Intl.NumberFormat("en-US", { minimumFractionDigits: isFloat && floatVal ? 2 : 0 })
    .format(nAmt)
    .toString();

  return {
    value: value,
    suffix,
    withSuffix: value + suffix,
  };
};


export const getCurrentHostname = (AppHost: string = window.location.hostname) => {
  const { domain, domainWithoutSuffix, subdomain } = parse(AppHost);

  const devDomains = ['maverick333', 'maveick111'];

  // Dev hosts keep legacy S3 folder naming: {subdomain}-{nameWithoutTld}
  if (devDomains.includes(domainWithoutSuffix ?? '') && subdomain) {
    return `${subdomain}-${domainWithoutSuffix}`;
  }

  // Production: full registrable domain, e.g. 404reddyanna.com
  return domain ?? '';
};

export const getRootOrigin = (origin: string = window.location.origin) => {
  try {
    const url = new URL(origin);
    const { domain, isIp } = parse(url.hostname);

    // Keep original for localhost/IP/unparseable hosts
    if (!domain || isIp) return url.origin;

    return `${url.protocol}//${domain}`;
  } catch {
    return window.location.origin;
  }
};


export const isValidUrl = async (newUrl?: string) => {
  try {
    if (!newUrl) return false;
    await axios.get(newUrl, { responseType: 'arraybuffer' });

    return true;
  } catch {
    return false;
  }
};
